import * as React from "react";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  useAuth,
  useOrganization,
  UserButton,
  OrganizationSwitcher,
  OrganizationList,
  SignInButton,
} from "@clerk/react";
import { newWebSocketRpcSession } from "capnweb";
import type {
  PublicApiStub,
  ItemPhase,
  ItemVersion,
  ActionLogEntry,
  Integration,
  OrgUser,
  Post,
  SignOff,
  Loadable,
  Paginated,
} from "scantling-common";
import { ItemHoverCard } from "./item_hover_card.js";
import { PostHoverCard } from "./post_hover_card.js";
import { IntegrationHoverCard } from "./integration_hover_card.js";
import { Redirect, Link, useParams, useLocation } from "wouter";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Download,
  PencilSimple,
  Check,
  ListBullets,
  Calendar,
  Upload,
  BookOpen,
} from "@phosphor-icons/react";

interface TileProps {
  children: ReactNode;
  href: string;
}

export const Tile = ({ children, href }: TileProps) => {
  return <Link href={href}>{children}</Link>;
};

interface PhaseTileProps {
  children: ReactNode;
  phase: string;
}

const isItemPhaseType = (phase: string): phase is ItemPhase => {
  return ["inbox", "editing", "signoff", "backlog"].includes(phase);
};

const isPostPhaseType = (phase: string): boolean => {
  return ["scheduling", "outbox"].includes(phase);
};

const PHASE_ICONS: Record<string, React.ComponentType<{ size?: number }>> = {
  inbox: Download,
  editing: PencilSimple,
  signoff: Check,
  backlog: ListBullets,
  scheduling: Calendar,
  outbox: Upload,
};

interface PhaseChipProps {
  phase: string;
}

const PHASE_DESCRIPTIONS: Record<string, string> = {
  inbox: "New items waiting to be triaged and assigned.",
  editing: "Items currently being worked on by the team.",
  signoff: "Items awaiting approval before publishing.",
  backlog: "Items saved for later prioritization.",
  scheduling: "Posts waiting to be scheduled for publication.",
  outbox: "Items ready to be published or exported.",
};

export const PhaseChip = ({ phase }: PhaseChipProps) => {
  const Icon = PHASE_ICONS[phase];
  const description = PHASE_DESCRIPTIONS[phase];

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <Badge variant="outline" className="cursor-help">
          {Icon && <Icon size={12} />}
          <span className="capitalize">{phase}</span>
        </Badge>
      </HoverCardTrigger>
      <HoverCardContent>
        <p className="mb-2">{description ?? `The ${phase} phase.`}</p>
        <Button variant="link" size="xs" asChild className="h-auto p-0">
          <Link href={`/docs/phases#${phase}`}>
            <BookOpen />
            Learn more
          </Link>
        </Button>
      </HoverCardContent>
    </HoverCard>
  );
};

const ItemPhaseBadge = ({
  children,
  phase,
}: {
  children: ReactNode;
  phase: ItemPhase;
}) => {
  const count = usePostCount(phase);

  return (
    <div className="relative inline-flex">
      {children}
      {count !== null && count > 0 && (
        <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
          {count > 9 ? "9+" : count}
        </span>
      )}
    </div>
  );
};

const PostPhaseBadge = ({
  children,
  phase,
}: {
  children: ReactNode;
  phase: string;
}) => {
  const count = usePostPhaseCount(phase);

  return (
    <div className="relative inline-flex">
      {children}
      {count !== null && count > 0 && (
        <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
          {count > 9 ? "9+" : count}
        </span>
      )}
    </div>
  );
};

const PhaseIconWithBadge = ({
  children,
  phase,
}: {
  children: ReactNode;
  phase: string;
}) => {
  if (isItemPhaseType(phase)) {
    return <ItemPhaseBadge phase={phase}>{children}</ItemPhaseBadge>;
  }
  if (isPostPhaseType(phase)) {
    return <PostPhaseBadge phase={phase}>{children}</PostPhaseBadge>;
  }
  return <div className="relative inline-flex">{children}</div>;
};

export const PhaseTile = ({ children, phase }: PhaseTileProps) => {
  const { orgSlug, itemSlug } = useParams<{
    orgSlug: string;
    itemSlug?: string;
  }>();
  const [location] = useLocation();
  const { getToken } = useAuth();
  const pool = useApiOptional();
  const [itemPhase, setItemPhase] = useState<string | null>(null);

  useEffect(() => {
    if (!itemSlug || !pool) return;

    const fetchItemPhase = async () => {
      const api = pool.acquire();
      try {
        const token = await getToken();
        if (!token) return;
        const authedApi = await api.authenticate(token);
        const orgApi = authedApi.org;
        if (!orgApi) return;
        const result = await orgApi.listItemVersions({
          search: `slug:${itemSlug}`,
        });
        if (result.entries.length > 0) {
          const latest = result.entries.reduce((a, b) =>
            a.version > b.version ? a : b,
          );
          setItemPhase(latest.phase);
        }
      } catch {
        // Ignore errors
      } finally {
        pool.release();
      }
    };
    fetchItemPhase();
  }, [itemSlug, pool, getToken]);

  const isActive = location === `/o/${orgSlug}/${phase}`;
  const isItemPhase = itemSlug && itemPhase === phase;

  return (
    <Tile href={`/o/${orgSlug}/${phase}`}>
      <div
        className={`relative flex flex-col items-center justify-center rounded-none px-4 py-2 transition-all hover:bg-muted hover:text-foreground active:translate-y-px ${isActive ? "bg-muted text-foreground" : "text-foreground/60"}`}
      >
        <PhaseIconWithBadge phase={phase}>{children}</PhaseIconWithBadge>
        <div className="text-xs font-medium capitalize">{phase}</div>
        {isItemPhase && (
          <div className="absolute bottom-0 left-1/2 h-0.5 w-6 -translate-x-1/2 bg-primary" />
        )}
      </div>
    </Tile>
  );
};

interface ItemSlugProps {
  slug: string;
  version?: number;
  item?: ItemVersion;
}

export const ItemSlug = ({ slug, version, item: itemProp }: ItemSlugProps) => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const search = itemProp
    ? undefined
    : version !== undefined
      ? `slug:${slug} version:${version}`
      : `slug:${slug}`;
  const fetched = useListItemVersions(search);
  if (fetched.error) throw new Error(fetched.error);

  let item = itemProp;
  if (!item && fetched.data?.entries.length) {
    item =
      version !== undefined
        ? fetched.data.entries[0]
        : fetched.data.entries.reduce((a, b) =>
            a.version > b.version ? a : b,
          );
  }

  const link = (
    <Link
      href={`/o/${orgSlug}/i/${slug}`}
      className="font-mono hover:underline"
    >
      {slug}
      {version !== undefined ? `@v${version}` : ""}
    </Link>
  );

  if (item) {
    return <ItemHoverCard item={item}>{link}</ItemHoverCard>;
  }

  return link;
};

interface DateDisplayProps {
  value: string | null | undefined;
  withTime?: boolean;
  fallback?: ReactNode;
  className?: string;
}

export const DateDisplay = ({
  value,
  withTime = true,
  fallback = null,
  className,
}: DateDisplayProps) => {
  if (!value) return <>{fallback}</>;
  const date = new Date(value);
  const text = withTime ? date.toLocaleString() : date.toLocaleDateString();
  return (
    <time
      dateTime={date.toISOString()}
      title={date.toLocaleString()}
      className={className}
    >
      {text}
    </time>
  );
};

interface CountdownDisplayProps {
  value: string | null | undefined;
  fallback?: React.ReactNode;
  className?: string;
}

export const CountdownDisplay = ({
  value,
  fallback = null,
  className,
}: CountdownDisplayProps) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  if (!value) return <>{fallback}</>;

  const target = new Date(value).getTime();
  const diff = target - now;
  const absDiff = Math.abs(diff);
  const isPast = diff < 0;

  const seconds = Math.floor(absDiff / 1000) % 60;
  const minutes = Math.floor(absDiff / (1000 * 60)) % 60;
  const hours = Math.floor(absDiff / (1000 * 60 * 60)) % 24;
  const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));

  let text: string;
  if (days > 0) {
    text = `${days}d ${hours}h`;
  } else if (hours > 0) {
    text = `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    text = `${minutes}m ${seconds}s`;
  } else if (seconds > 0) {
    text = `${seconds}s`;
  } else {
    text = "now";
  }

  if (isPast && text !== "now") {
    text = `${text} ago`;
  }

  return (
    <time
      dateTime={new Date(value).toISOString()}
      title={new Date(value).toLocaleString()}
      className={className}
    >
      {text}
    </time>
  );
};

interface PostSlugProps {
  slug: string;
  post?: Post;
}

export const PostSlug = ({ slug, post: postProp }: PostSlugProps) => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const search = postProp ? undefined : `slug:${slug}`;
  const fetched = useListPosts(search);
  if (fetched.error) throw new Error(fetched.error);

  const post = postProp ?? fetched.data?.entries[0];

  const link = (
    <Link
      href={`/o/${orgSlug}/p/${slug}`}
      className="font-mono hover:underline"
    >
      {slug}
    </Link>
  );

  if (post) {
    return <PostHoverCard post={post}>{link}</PostHoverCard>;
  }

  return link;
};

interface IntegrationSlugProps {
  slug: string;
  integration?: Integration;
}

export const IntegrationSlug = ({
  slug,
  integration: integrationProp,
}: IntegrationSlugProps) => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const search = integrationProp ? undefined : `slug:${slug}`;
  const fetched = useListIntegrations(search);
  if (fetched.error) throw new Error(fetched.error);

  const integration = integrationProp ?? fetched.data?.entries[0];

  const link = (
    <Link
      href={`/o/${orgSlug}/n/${slug}`}
      className="font-mono hover:underline"
    >
      {slug}
    </Link>
  );

  if (integration) {
    return (
      <IntegrationHoverCard integration={integration}>
        {link}
      </IntegrationHoverCard>
    );
  }

  return link;
};

export const AccountNavSkeleton = () => {
  return (
    <div className="m-4 flex items-center gap-4">
      <div className="h-8 w-32 animate-pulse rounded bg-gray-200" />
      <div className="h-8 w-8 animate-pulse rounded-full bg-gray-200" />
    </div>
  );
};

export const AccountNav = () => {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) {
    return <AccountNavSkeleton />;
  }
  return (
    <div className="m-4 flex items-center gap-4">
      <OrganizationSwitcher
        afterCreateOrganizationUrl="/o/:slug"
        afterSelectOrganizationUrl="/o/:slug"
      />
      {isSignedIn ? <UserButton /> : <SignInButton />}
    </div>
  );
};

class ConnectionPool {
  private connection: PublicApiStub | null = null;
  private refCount = 0;

  acquire(): PublicApiStub {
    if (!this.connection) {
      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${wsProtocol}//${window.location.host}/api`;
      this.connection = newWebSocketRpcSession(url);
    }
    this.refCount++;
    return this.connection.dup();
  }

  release(): void {
    this.refCount--;
    if (this.refCount === 0 && this.connection) {
      this.connection[Symbol.dispose]();
      this.connection = null;
    }
  }
}

const ApiContext = createContext<ConnectionPool | null>(null);

export function useApi(): ConnectionPool {
  const pool = useContext(ApiContext);
  if (!pool) {
    throw new Error("useApi must be used within WithApi");
  }
  return pool;
}

export function useApiOptional(): ConnectionPool | null {
  return useContext(ApiContext);
}

export function WithApi({ children }: { children: React.ReactNode }) {
  const pool = useMemo(() => new ConnectionPool(), []);
  return <ApiContext value={pool}>{children}</ApiContext>;
}

type CacheEntry = {
  state: Loadable<unknown>;
  subscribers: Set<() => void>;
  inflight?: Promise<void>;
  fetcher?: () => Promise<unknown>;
};

class ListCache {
  private entries = new Map<string, CacheEntry>();

  getState<T>(key: string): Loadable<T> {
    const entry = this.entries.get(key);
    return (entry?.state as Loadable<T>) ?? { isLoading: true };
  }

  subscribe(key: string, cb: () => void): () => void {
    const entry = this.ensure(key);
    entry.subscribers.add(cb);
    return () => {
      entry.subscribers.delete(cb);
    };
  }

  revalidate<T>(key: string, fetcher: () => Promise<T>): Promise<void> {
    const entry = this.ensure(key);
    entry.fetcher = fetcher as () => Promise<unknown>;
    if (entry.inflight) return entry.inflight;
    entry.inflight = (async () => {
      try {
        const data = await fetcher();
        entry.state = { isLoading: false, data };
      } catch (err) {
        entry.state = {
          isLoading: false,
          error: err instanceof Error ? err.message : String(err),
        };
      } finally {
        entry.inflight = undefined;
        for (const cb of entry.subscribers) cb();
      }
    })();
    return entry.inflight;
  }

  invalidate(key: string): void {
    const entry = this.entries.get(key);
    if (!entry?.fetcher) {
      this.entries.delete(key);
      return;
    }
    entry.inflight = undefined;
    this.revalidate(key, entry.fetcher);
  }

  invalidatePrefix(prefix: string): void {
    for (const key of this.entries.keys()) {
      if (key.startsWith(prefix)) this.invalidate(key);
    }
  }

  private ensure(key: string): CacheEntry {
    let entry = this.entries.get(key);
    if (!entry) {
      entry = { state: { isLoading: true }, subscribers: new Set() };
      this.entries.set(key, entry);
    }
    return entry;
  }
}

const ListCacheContext = createContext<ListCache | null>(null);

export function useListCache(): ListCache {
  const cache = useContext(ListCacheContext);
  if (!cache) {
    throw new Error("useListCache must be used within WithListCache");
  }
  return cache;
}

export function WithListCache({ children }: { children: React.ReactNode }) {
  const cache = useMemo(() => new ListCache(), []);
  return <ListCacheContext value={cache}>{children}</ListCacheContext>;
}

function useSwr<T>(key: string, fetcher: () => Promise<T>): Loadable<T> {
  const cache = useListCache();
  const [, setTick] = useState(0);
  const fetcherRef = React.useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    const unsub = cache.subscribe(key, () => setTick(n => n + 1));
    cache.revalidate(key, () => fetcherRef.current());
    return unsub;
  }, [cache, key]);

  return cache.getState<T>(key);
}

function queryKey(search?: string, cursor?: string, limit?: number): string {
  return `${search ?? ""}:${cursor ?? ""}:${limit ?? ""}`;
}

export function useListItemVersions(
  search?: string,
  cursor?: string,
  limit?: number,
): Loadable<Paginated<ItemVersion>> {
  const { getToken } = useAuth();
  const pool = useApi();
  const key = `listItemVersions:${queryKey(search, cursor, limit)}`;
  return useSwr(key, async () => {
    const api = pool.acquire();
    try {
      const token = await getToken();
      if (!token) throw new Error("not authenticated");
      const authedApi = await api.authenticate(token);
      const orgApi = authedApi.org;
      if (!orgApi) throw new Error("no org");
      return await orgApi.listItemVersions({ search, cursor, limit });
    } finally {
      pool.release();
    }
  });
}

export function useListActions(
  search?: string,
  cursor?: string,
  limit?: number,
): Loadable<Paginated<ActionLogEntry>> {
  const { getToken } = useAuth();
  const pool = useApi();
  const key = `listActions:${queryKey(search, cursor, limit)}`;
  return useSwr(key, async () => {
    const api = pool.acquire();
    try {
      const token = await getToken();
      if (!token) throw new Error("not authenticated");
      const authedApi = await api.authenticate(token);
      const orgApi = authedApi.org;
      if (!orgApi) throw new Error("no org");
      return await orgApi.listActions({ search, cursor, limit });
    } finally {
      pool.release();
    }
  });
}

export function useListIntegrations(
  search?: string,
  cursor?: string,
  limit?: number,
): Loadable<Paginated<Integration>> {
  const { getToken } = useAuth();
  const pool = useApi();
  const key = `listIntegrations:${queryKey(search, cursor, limit)}`;
  return useSwr(key, async () => {
    const api = pool.acquire();
    try {
      const token = await getToken();
      if (!token) throw new Error("not authenticated");
      const authedApi = await api.authenticate(token);
      const orgApi = authedApi.org;
      if (!orgApi) throw new Error("no org");
      return await orgApi.listIntegrations({ search, cursor, limit });
    } finally {
      pool.release();
    }
  });
}

export function useListUsers(
  search?: string,
  cursor?: string,
  limit?: number,
): Loadable<Paginated<OrgUser>> {
  const { getToken } = useAuth();
  const pool = useApi();
  const key = `listUsers:${queryKey(search, cursor, limit)}`;
  return useSwr(key, async () => {
    const api = pool.acquire();
    try {
      const token = await getToken();
      if (!token) throw new Error("not authenticated");
      const authedApi = await api.authenticate(token);
      const orgApi = authedApi.org;
      if (!orgApi) throw new Error("no org");
      return await orgApi.listUsers({ search, cursor, limit });
    } finally {
      pool.release();
    }
  });
}

export function useListPosts(
  search?: string,
  cursor?: string,
  limit?: number,
): Loadable<Paginated<Post>> {
  const { getToken } = useAuth();
  const pool = useApi();
  const key = `listPosts:${queryKey(search, cursor, limit)}`;
  return useSwr(key, async () => {
    const api = pool.acquire();
    try {
      const token = await getToken();
      if (!token) throw new Error("not authenticated");
      const authedApi = await api.authenticate(token);
      const orgApi = authedApi.org;
      if (!orgApi) throw new Error("no org");
      return await orgApi.listPosts({ search, cursor, limit });
    } finally {
      pool.release();
    }
  });
}

export function useListSignOffs(
  search?: string,
  cursor?: string,
  limit?: number,
): Loadable<Paginated<SignOff>> {
  const { getToken } = useAuth();
  const pool = useApi();
  const key = `listSignOffs:${queryKey(search, cursor, limit)}`;
  return useSwr(key, async () => {
    const api = pool.acquire();
    try {
      const token = await getToken();
      if (!token) throw new Error("not authenticated");
      const authedApi = await api.authenticate(token);
      const orgApi = authedApi.org;
      if (!orgApi) throw new Error("no org");
      return await orgApi.listSignOffs({ search, cursor, limit });
    } finally {
      pool.release();
    }
  });
}

export function useInvalidateLists(): (...endpoints: string[]) => void {
  const cache = useListCache();
  return useCallback(
    (...endpoints: string[]) => {
      for (const endpoint of endpoints) {
        cache.invalidatePrefix(`${endpoint}:`);
      }
    },
    [cache],
  );
}

export function usePostCount(phase: ItemPhase): number | null {
  const { data } = useListItemVersions(`phase:${phase}`);
  if (!data) return null;
  return data.entries.length;
}

export function usePostPhaseCount(phase: string): number | null {
  const { data } = useListPosts(`phase:${phase}`);
  if (!data) return null;
  return data.entries.length;
}

/**
 * Helper to build a login URL with a return URL encoded in the query string.
 * @param returnTo - The URL to return to after login (defaults to current path)
 */
export function buildLoginUrl(returnTo?: string): string {
  const target = returnTo ?? window.location.pathname + window.location.search;
  return `/login?returnTo=${encodeURIComponent(target)}`;
}

/**
 * A wrapper component that requires authentication.
 * Redirects to login with the current URL encoded if not authenticated.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) {
    return null;
  }

  if (!isSignedIn) {
    return <Redirect to={buildLoginUrl()} />;
  }

  return <>{children}</>;
}

export function OrgSync({ children }: { children: React.ReactNode }) {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { organization, isLoaded } = useOrganization();

  if (!isLoaded) {
    return null;
  }

  if (!organization || organization.slug !== orgSlug) {
    return (
      <RequireAuth>
        <div className="mx-auto max-w-md p-6">
          <p className="mb-4 text-gray-600">
            Sorry, Organization <span className="font-medium">{orgSlug}</span>{" "}
            is not valid.
          </p>
          <OrganizationList
            afterCreateOrganizationUrl="/o/:slug"
            afterSelectOrganizationUrl="/o/:slug"
          />
        </div>
      </RequireAuth>
    );
  }

  return (
    <WithApi>
      <RequireAuth>{children}</RequireAuth>
    </WithApi>
  );
}
